/**
 * Local persistence. Key names are unchanged from the prototype so a guest who
 * used the earlier build keeps their progress.
 *
 * Guest progress stays here permanently: signing out returns to it untouched.
 */
import { nowSeconds, readBox, type Boxes } from "../../shared/srs";
import type { DeckId } from "../../shared/vocab";

const KEY_USER = "gk-vocab-user";
const KEY_NAME = "gk-vocab-name";
const KEY_DECK = "gk-vocab-deck";
const KEY_PLAYER = "gk-vocab-player-id";
const KEY_MUTED = "gk-vocab-muted";

function read(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function write(key: string, value: string): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // private mode, quota, disabled storage — never fatal
  }
}

/** `gk-vocab-boxes-{email-}{deck}`; the guest has no email segment. */
export function boxesKey(deck: DeckId, user: string): string {
  return `gk-vocab-boxes-${user ? `${user}-` : ""}${deck}`;
}

/**
 * Reads the map in either shape: `{ l, t }` boxes, or the plain levels the
 * earlier build stored. Anything unreadable is dropped word by word rather
 * than discarding the whole map.
 */
export function loadBoxes(deck: DeckId, user: string): Boxes {
  try {
    const raw = localStorage.getItem(boxesKey(deck, user));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const now = nowSeconds();
    const boxes: Boxes = {};
    for (const [word, value] of Object.entries(parsed as Record<string, unknown>)) {
      const box = readBox(value, now);
      if (box) boxes[word] = box;
    }
    return boxes;
  } catch {
    return {};
  }
}

export function saveBoxes(deck: DeckId, user: string, boxes: Boxes): void {
  try {
    localStorage.setItem(boxesKey(deck, user), JSON.stringify(boxes));
  } catch {
    // ignore
  }
}

export const storedUser = () => read(KEY_USER);
export const setStoredUser = (v: string) => write(KEY_USER, v);

export const storedName = () => read(KEY_NAME);
export const setStoredName = (v: string) => write(KEY_NAME, v);

export const storedDeck = () => read(KEY_DECK);
export const setStoredDeck = (v: string) => write(KEY_DECK, v);

/** Pronunciation playback is on unless the learner turned it off. */
export const storedMuted = () => read(KEY_MUTED) === "1";
export const setStoredMuted = (v: boolean) => write(KEY_MUTED, v ? "1" : "");

/** The duel this tab is in, so a reload can walk back into it. */
export type ActiveRoom = { code: string; name: string; deckId: DeckId };

const KEY_ROOM = "gk-vocab-room";

export function activeRoom(): ActiveRoom | null {
  try {
    const raw = sessionStorage.getItem(KEY_ROOM);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveRoom;
    return typeof parsed?.code === "string" ? parsed : null;
  } catch {
    return null;
  }
}

export function setActiveRoom(room: ActiveRoom | null): void {
  try {
    if (room) sessionStorage.setItem(KEY_ROOM, JSON.stringify(room));
    else sessionStorage.removeItem(KEY_ROOM);
  } catch {
    // ignore
  }
}

/**
 * Stable for the life of the tab. Survives a reload, which — together with the
 * active room above — is what lets a refreshed page walk straight back into a
 * duel instead of being treated as a third participant.
 */
export function playerId(): string {
  try {
    let id = sessionStorage.getItem(KEY_PLAYER);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(KEY_PLAYER, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}
